/**
 * Shared TSV (tab-separated values) detection and conversion utilities.
 * Used by the task modal and the regulation/article rich-text editors.
 */

import { colors, lightPalette, darkPalette, getContrastForBackground } from "@/theme/colors";
const CH_TO_PX = 8;
const MIN_COL_WIDTH = 60;
const MAX_COL_WIDTH = 320;
const CELL_PAD_PX = 20;

// Lazily-created canvas 2D context shared across `recalcCol` invocations
// so we can measure real glyph widths instead of guessing with an
// average-character heuristic.
let _measureCtxInstance;
function _measureCtx() {
  if (_measureCtxInstance !== undefined) return _measureCtxInstance;
  if (typeof document === "undefined") {
    _measureCtxInstance = null;
    return null;
  }
  try {
    _measureCtxInstance = document.createElement("canvas").getContext("2d") || null;
  } catch {
    _measureCtxInstance = null;
  }
  return _measureCtxInstance;
}

/**
 * Proper TSV parser that handles RFC-4180-style quoted fields.
 *
 * Google Sheets (and Excel) wrap cell values in double-quotes whenever
 * the cell contains tabs, commas, or — crucially — **newline characters**.
 * Literal double-quotes inside a value are escaped as `""`. A naïve
 * `split("\n")` approach breaks those cells into spurious rows that have
 * no tabs, causing `looksLikeTsv` to return false and `parseTsv` to
 * produce garbled output.
 *
 * This parser walks the raw text character-by-character, respecting the
 * open/close state of `"..."` blocks, so multi-line cell values are
 * correctly collected into a single field.
 */
function parseQuotedTsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  // Normalise \r\n → \n so the rest of the logic only deals with \n.
  const s = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          // Escaped double-quote inside a quoted field → literal "
          field += '"';
          i++; // skip the second "
        } else {
          // Closing quote
          inQuotes = false;
        }
      } else {
        field += ch; // any character (including \n and \t) is literal
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === "\t") {
        row.push(field);
        field = "";
      } else if (ch === "\n") {
        row.push(field);
        field = "";
        // Only emit non-empty rows (skip blank separator lines)
        if (row.some(f => f.trim().length > 0)) {
          rows.push(row);
        }
        row = [];
      } else {
        field += ch;
      }
    }
  }
  // Flush the final field / row
  row.push(field);
  if (row.some(f => f.trim().length > 0)) {
    rows.push(row);
  }
  return rows;
}

/** Detect whether clipboard text looks like a TSV table (e.g. pasted from Google Sheets / Excel). */
export function looksLikeTsv(text) {
  if (!text.trim()) return false;
  const rows = parseQuotedTsv(text);
  // Need at least 2 rows and at least one row with more than 1 column.
  return rows.length >= 2 && rows.some(r => r.length > 1);
}

// ── Markdown output (for task body field) ──

export function tsvToMarkdown(tsv) {
  const rows = parseQuotedTsv(tsv);
  if (rows.length === 0) return "";
  const colCount = Math.max(...rows.map(r => r.length));
  const padded = rows.map(r => {
    while (r.length < colCount) r.push("");
    return r;
  });
  const header = `| ${padded[0].join(" | ")} |`;
  const sep = `| ${padded[0].map(() => "---").join(" | ")} |`;
  const body = padded.slice(1).map(r => `| ${r.join(" | ")} |`).join("\n");
  return [header, sep, body].filter(Boolean).join("\n");
}

// ── Helpers ──

function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function parseTsv(tsv) {
  const rows = parseQuotedTsv(tsv);
  if (rows.length === 0) return {
    rows: [],
    colCount: 0
  };
  const colCount = Math.max(...rows.map(r => r.length));
  const padded = rows.map(r => {
    while (r.length < colCount) r.push("");
    return r;
  });
  return {
    rows: padded,
    colCount
  };
}

// ── Column width calculation with outlier detection ──

function computeColumnMetrics(rows, colIndex) {
  const lengths = rows.map(r => r[colIndex].trim().length);
  if (lengths.length === 0) return {
    width: MIN_COL_WIDTH,
    charLimit: Infinity
  };

  // Average length with 10% buffer — only values exceeding this are outliers
  const avg = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  const charLimit = Math.max(6, Math.ceil(avg * 1.1));

  // Width sized to fit charLimit so non-outlier content is never CSS-truncated
  const rawWidth = Math.ceil(charLimit * CH_TO_PX) + CELL_PAD_PX;
  const width = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, rawWidth));
  return {
    width,
    charLimit
  };
}
function buildCell(text, tag, metrics) {
  const raw = text.trim();
  const {
    width
  } = metrics;
  const display = escapeHtml(raw);
  const style = `min-width:${width}px;max-width:${width}px;`;
  return `<${tag} contenteditable="true" style="${style}">${display}</${tag}>`;
}
function buildColgroup(metrics) {
  return `<colgroup>${metrics.map(m => `<col style="width:${m.width}px;min-width:${m.width}px;">`).join("")}</colgroup>`;
}

// ── HTML table output (for contentEditable editors) ──

export function tsvToHtmlTable(tsv) {
  const {
    rows,
    colCount
  } = parseTsv(tsv);
  if (rows.length === 0 || colCount === 0) return "";
  const colMetrics = [];
  for (let c = 0; c < colCount; c++) {
    colMetrics.push(computeColumnMetrics(rows, c));
  }
  const uid = "pt_" + Math.random().toString(36).slice(2, 10);
  const colgroup = buildColgroup(colMetrics);
  const dataRows = rows.map((r, rowIdx) => {
    const cls = rowIdx === 0 ? ` class="default-header"` : "";
    const cells = r.map((cell, ci) => buildCell(cell, "td", colMetrics[ci])).join("");
    return `<tr${cls}>${cells}</tr>`;
  }).join("");
  const table = `<table class="pasted-table">${colgroup}<tbody>${dataRows}</tbody></table>`;
  return [`<div class="pasted-table-wrap" id="${uid}" contenteditable="false" data-label-pos="top" data-labels-on="false" data-wrap="on">`, `<div class="pasted-table-scroll">${table}</div>`, `</div>`].join("");
}

// ── Parse markdown table back to rendered HTML ──

export function markdownTableToHtml(text) {
  const lines = text.split("\n").map(l => l.trim()).filter(l => l.length > 0);
  if (lines.length < 3) return null;
  const isTableRow = l => l.startsWith("|") && l.endsWith("|");
  const isSepRow = l => /^\|[\s\-:|]+\|$/.test(l) && l.slice(1, -1).split("|").every(c => /^[\s\-:]+$/.test(c));
  if (!isTableRow(lines[0]) || !isSepRow(lines[1])) return null;
  for (let i = 2; i < lines.length; i++) {
    if (!isTableRow(lines[i])) return null;
  }
  const parseCells = line => line.slice(1, -1).split("|").map(c => c.trim());
  const allRows = [parseCells(lines[0]), ...lines.slice(2).map(parseCells)];
  const colCount = Math.max(...allRows.map(r => r.length));
  const padded = allRows.map(r => {
    while (r.length < colCount) r.push("");
    return r;
  });
  const colMetrics = [];
  for (let c = 0; c < colCount; c++) {
    colMetrics.push(computeColumnMetrics(padded, c));
  }
  const colgroup = buildColgroup(colMetrics);
  const headerRow = `<tr>${padded[0].map((c, ci) => buildCell(c, "th", colMetrics[ci])).join("")}</tr>`;
  const bodyRows = padded.slice(1).map(r => `<tr>${r.map((c, ci) => buildCell(c, "td", colMetrics[ci])).join("")}</tr>`).join("");
  return [`<div class="pasted-table-wrap" data-wrap="on">`, `<div class="pasted-table-scroll">`, `<table class="pasted-table">${colgroup}<thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`, `</div></div>`].join("");
}

// ══════════════════════════════════════════════════════════════════════════════
// Interactive table behaviors (toolbar, labels, add column, resize, delete)
// ══════════════════════════════════════════════════════════════════════════════

const RESIZE_ZONE_PX = 6;
let _ghostLine = null;
function ensureGhostLine() {
  if (_ghostLine) return _ghostLine;
  const el = document.createElement("div");
  Object.assign(el.style, {
    position: "fixed",
    display: "none",
    zIndex: "200",
    width: "2px",
    background: "#1976d2",
    pointerEvents: "none",
    opacity: "0.7"
  });
  document.body.appendChild(el);
  _ghostLine = el;
  return el;
}
function findWrap(el) {
  return el?.closest?.(".pasted-table-wrap");
}
function tblOf(wrap) {
  return wrap.querySelector("table");
}
function cellColIdx(cell) {
  let i = 0;
  let s = cell.previousElementSibling;
  while (s) {
    i++;
    s = s.previousElementSibling;
  }
  return i;
}
function recalcCol(tbl, ci, w) {
  const wrap = tbl.closest(".pasted-table-wrap");
  const wrapOff = wrap?.getAttribute("data-wrap") === "off";
  const applySz = el => {
    el.style.minWidth = `${w}px`;
    el.style.maxWidth = `${w}px`;
  };
  // Use the canvas 2D API to measure actual rendered text width per
  // cell so we only insert the ellipsis when the full string would
  // truly overflow the cell's content box. Falling back to a flat
  // average-character heuristic (the previous `lim = w / CH_TO_PX`
  // approach) consistently truncated wider columns far too aggressively
  // because real proportional font widths vary by ~2x across glyphs.
  const ctx = _measureCtx();
  const measureBudget = el => {
    const cs = window.getComputedStyle(el);
    if (ctx) {
      // Compose a font shorthand the canvas understands.
      ctx.font = `${cs.fontStyle || "normal"} ${cs.fontVariant || "normal"} ${cs.fontWeight || "400"} ${cs.fontSize || "14px"} ${cs.fontFamily || "sans-serif"}`;
    }
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    // `w` is the constrained content-box width (set via min/max-width
    // with the default `box-sizing: content-box`). Padding does not
    // eat into it, so the budget for text is simply `w`. Subtract a
    // 1px hairline to absorb sub-pixel rounding noise.
    void padL;
    void padR;
    return Math.max(0, w - 1);
  };
  const fitText = (full, budget) => {
    if (!ctx || budget <= 0) return full;
    if (ctx.measureText(full).width <= budget) return full;
    const ell = "…";
    const ellW = ctx.measureText(ell).width;
    let lo = 0;
    let hi = full.length;
    while (lo < hi) {
      const mid = lo + hi + 1 >> 1;
      if (ctx.measureText(full.slice(0, mid)).width + ellW <= budget) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    return lo > 0 ? full.slice(0, lo) + ell : ell;
  };
  // Truncated cells: store the full text on `data-full-text` so the
  // custom 500ms tooltip can read it on hover. We deliberately avoid the
  // native `title` attribute because the browser-controlled tooltip
  // delay is significantly longer (700ms+ on most platforms).
  const applyCellContent = el => {
    // Save and exclude any injected delete-row button from the text reading,
    // since its "×" text would otherwise get baked into the cell content.
    const delBtn = el.querySelector(".pt-del-row");
    const textEl = delBtn ? (() => {
      const clone = el.cloneNode(true);
      clone.querySelector(".pt-del-row")?.remove();
      return clone;
    })() : el;
    const full = el.getAttribute("data-full-text") || el.getAttribute("title") || textEl.textContent || "";
    // Apply size first so getComputedStyle reflects the new width.
    applySz(el);
    if (wrapOff) {
      const budget = measureBudget(el);
      const display = fitText(full, budget);
      if (display !== full) {
        el.textContent = display;
        el.setAttribute("data-full-text", full);
      } else {
        el.textContent = full;
        el.removeAttribute("data-full-text");
      }
    } else {
      el.textContent = full;
      el.removeAttribute("data-full-text");
    }
    // Restore the delete-row button that was wiped by textContent assignment.
    if (delBtn) el.appendChild(delBtn);
    // Strip any legacy native title to prevent a duplicate tooltip.
    el.removeAttribute("title");
  };
  tbl.querySelectorAll("tbody tr").forEach(tr => {
    const c = tr.children[ci];
    if (c) applyCellContent(c);
  });
  const thRow = tbl.querySelector("thead tr");
  if (thRow && thRow.children[ci]) applySz(thRow.children[ci]);
  const col = tbl.querySelectorAll("colgroup col")[ci];
  if (col) {
    col.style.width = `${w}px`;
    col.style.minWidth = `${w}px`;
  }
}
function addColumnToTable(tbl) {
  const w = MIN_COL_WIDTH;
  const cg = tbl.querySelector("colgroup");
  if (cg) {
    const col = document.createElement("col");
    col.style.width = `${w}px`;
    col.style.minWidth = `${w}px`;
    cg.appendChild(col);
  }
  const thead = tbl.querySelector("thead tr");
  if (thead) {
    const th = document.createElement("th");
    th.setAttribute("contenteditable", "true");
    th.setAttribute("data-placeholder", "Label");
    th.className = "label-cell";
    th.style.minWidth = `${w}px`;
    th.style.maxWidth = `${w}px`;
    thead.appendChild(th);
  }
  tbl.querySelectorAll("tbody tr").forEach(tr => {
    const td = document.createElement("td");
    td.setAttribute("contenteditable", "true");
    td.style.minWidth = `${w}px`;
    td.style.maxWidth = `${w}px`;
    tr.appendChild(td);
  });
}
function addRowToTable(tbl) {
  const tbody = tbl.querySelector("tbody");
  if (!tbody) return;
  const colCount = tbl.querySelectorAll("colgroup col").length || (tbody.firstElementChild ? tbody.firstElementChild.children.length : 1);
  const tr = document.createElement("tr");
  for (let i = 0; i < colCount; i++) {
    const td = document.createElement("td");
    const col = tbl.querySelectorAll("colgroup col")[i];
    const w = col ? parseInt(col.style.width) || MIN_COL_WIDTH : MIN_COL_WIDTH;
    td.style.minWidth = `${w}px`;
    td.style.maxWidth = `${w}px`;
    td.setAttribute("contenteditable", "true");
    if (tbl.classList.contains("label-left") && i === 0) {
      td.className = "left-label-cell";
      td.setAttribute("data-placeholder", "Label");
    }
    tr.appendChild(td);
  }
  tbody.appendChild(tr);
}

// ── Label management ──

// Style props that travel with a header column when the label-header row
// is toggled on/off. The user's intent is that the *visual* header row
// (whichever one is currently styled as the header) keeps its fill while
// the underlying content moves into / out of a regular data row.
const HEADER_FILL_PROPS = ["background-color", "color"];
const cellFillStyles = cell => {
  const out = [];
  HEADER_FILL_PROPS.forEach(prop => {
    const v = cell.style.getPropertyValue(prop);
    if (v) out.push(`${prop}: ${v}`);
  });
  return out.join("; ");
};
const stripFillStyles = cell => {
  HEADER_FILL_PROPS.forEach(prop => cell.style.removeProperty(prop));
  if (!cell.getAttribute("style")) cell.removeAttribute("style");
};
function addTopLabels(tbl) {
  if (tbl.querySelector("thead")) return;
  const cols = tbl.querySelectorAll("colgroup col").length;
  if (cols === 0) return;
  // Capture the fill colors from the row that is *currently* acting as
  // the visual header (the first body row marked .default-header) so the
  // newly-inserted label row inherits them. Without this, toggling on
  // the label header would visually move the user's chosen fill colors
  // down into the first data row.
  const defRow = tbl.querySelector("tr.default-header");
  const inheritedFills = [];
  if (defRow) {
    Array.from(defRow.children).forEach(c => {
      inheritedFills.push(cellFillStyles(c));
    });
  }
  const thead = document.createElement("thead");
  const tr = document.createElement("tr");
  for (let i = 0; i < cols; i++) {
    const th = document.createElement("th");
    th.setAttribute("contenteditable", "true");
    th.setAttribute("data-placeholder", "Label");
    th.className = "label-cell";
    const styleStr = inheritedFills[i];
    if (styleStr) th.setAttribute("style", styleStr);
    tr.appendChild(th);
  }
  thead.appendChild(tr);
  const cg = tbl.querySelector("colgroup");
  tbl.insertBefore(thead, cg ? cg.nextSibling : tbl.firstChild);
  if (defRow) {
    // Strip the fills from the now-demoted body row so the colors don't
    // appear twice (once on the new label row, once on the data row).
    Array.from(defRow.children).forEach(c => stripFillStyles(c));
    defRow.classList.remove("default-header");
  }
}
function removeTopLabels(tbl) {
  // When tearing down the explicit label row, transfer its per-column
  // fill colors onto the first body row (which becomes the new visual
  // header via .default-header). Mirrors addTopLabels so toggling is
  // round-trip-safe.
  const thead = tbl.querySelector("thead");
  const headerCells = thead ? Array.from(thead.querySelectorAll("tr:first-child > th")) : [];
  const inheritedFills = headerCells.map(c => cellFillStyles(c));
  if (thead) thead.remove();
  const firstBodyRow = tbl.querySelector("tbody tr");
  if (firstBodyRow && inheritedFills.some(Boolean)) {
    Array.from(firstBodyRow.children).forEach((c, idx) => {
      const el = c;
      const styleStr = inheritedFills[idx];
      if (!styleStr) return;
      // Apply each prop individually so we don't blow away other
      // inline styles the cell may already have.
      styleStr.split(";").forEach(decl => {
        const [k, v] = decl.split(":").map(s => s.trim());
        if (k && v) el.style.setProperty(k, v);
      });
    });
  }
}
function getTopLabelTexts(tbl) {
  const thead = tbl.querySelector("thead tr");
  if (!thead) return [];
  return Array.from(thead.querySelectorAll("th")).map(th => th.textContent || "");
}

// Note: left-side row labels are not exposed in the editor UI. The
// `extractTableData`/`tableDataToHtml` helpers below still preserve them on
// read/write for backward compatibility with previously-saved content.

function toggleLabels(wrap) {
  const tbl = tblOf(wrap);
  if (!tbl) return;
  const labelsOn = wrap.getAttribute("data-labels-on") === "true";
  // Force-pin label position to the top: the editor only supports adding
  // column (top) headers via this control. Left-side row labels are
  // intentionally not exposed here.
  wrap.setAttribute("data-label-pos", "top");
  if (labelsOn) {
    removeTopLabels(tbl);
    const firstRow = tbl.querySelector("tbody tr");
    if (firstRow) firstRow.classList.add("default-header");
    wrap.setAttribute("data-labels-on", "false");
  } else {
    const defRow = tbl.querySelector("tr.default-header");
    if (defRow) defRow.classList.remove("default-header");
    addTopLabels(tbl);
    wrap.setAttribute("data-labels-on", "true");
  }
}

// ── Structured data extraction / generation ──

/**
 * Extract structured table data from a `.pasted-table-wrap` DOM element.
 * Works on both live DOM and elements created from innerHTML.
 */
export function extractTableData(wrap) {
  const labelsOn = wrap.getAttribute("data-labels-on") === "true";
  const labelPos = wrap.getAttribute("data-label-pos") || "top";
  const tbl = wrap.querySelector("table.pasted-table");

  // Column widths from colgroup
  const colEls = tbl ? Array.from(tbl.querySelectorAll("colgroup col")) : [];
  const columnWidths = colEls.map(col => parseInt(col.style.width) || MIN_COL_WIDTH);

  // Top labels from thead
  let topLabels;
  if (labelsOn && labelPos === "top" && tbl) {
    const ths = tbl.querySelectorAll("thead th");
    if (ths.length) topLabels = Array.from(ths).map(th => th.textContent || "");
  }

  // Left labels + data rows from tbody
  let leftLabels;
  const rows = [];
  if (tbl) {
    const hasLeft = labelsOn && labelPos === "left";
    tbl.querySelectorAll("tbody tr").forEach(tr => {
      const cells = Array.from(tr.children);
      if (hasLeft && cells.length > 0 && cells[0].classList.contains("left-label-cell")) {
        if (!leftLabels) leftLabels = [];
        leftLabels.push(cells[0].textContent || "");
        rows.push(cells.slice(1).map(c => c.textContent || ""));
      } else {
        rows.push(cells.map(c => c.textContent || ""));
      }
    });
    // If left labels enabled but we have no colgroup col for the label column,
    // remove the first columnWidth since it was for the label column
    if (hasLeft && columnWidths.length > 0 && rows.length > 0 && columnWidths.length > rows[0]?.length) {
      columnWidths.shift();
    }
  }
  return {
    rows,
    topLabels,
    leftLabels,
    columnWidths,
    labelsOn,
    labelPos
  };
}

/**
 * Generate `.pasted-table-wrap` HTML from structured table data.
 * Produces the same DOM structure as `tsvToHtmlTable` with label support.
 * All data cells are contenteditable for in-editor editing.
 */
export function tableDataToHtml(data) {
  const {
    rows,
    topLabels,
    leftLabels,
    columnWidths,
    labelsOn,
    labelPos
  } = data;
  const esc = s => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const uid = "pt_" + Math.random().toString(36).slice(2, 10);

  // Build colgroup — include left-label col if applicable
  const hasLeft = labelsOn && labelPos === "left" && leftLabels;
  const colgroupCols = [];
  if (hasLeft) {
    colgroupCols.push(`<col style="width:${MIN_COL_WIDTH}px;min-width:${MIN_COL_WIDTH}px;">`);
  }
  columnWidths.forEach(w => {
    colgroupCols.push(`<col style="width:${w}px;min-width:${w}px;">`);
  });
  const colgroup = `<colgroup>${colgroupCols.join("")}</colgroup>`;

  // Build thead (top labels)
  let thead = "";
  if (labelsOn && labelPos === "top" && topLabels) {
    const ths = topLabels.map(t => `<th contenteditable="true" data-placeholder="Label" class="label-cell">${esc(t)}</th>`).join("");
    thead = `<thead><tr>${ths}</tr></thead>`;
  }

  // Build tbody
  const bodyRows = rows.map((row, ri) => {
    const cls = !labelsOn && ri === 0 ? ' class="default-header"' : "";
    let cells = "";
    if (hasLeft && leftLabels) {
      const lbl = leftLabels[ri] ?? "";
      cells += `<td class="left-label-cell" contenteditable="true" data-placeholder="Label" style="min-width:${MIN_COL_WIDTH}px;max-width:${MIN_COL_WIDTH}px;">${esc(lbl)}</td>`;
    }
    cells += row.map((val, ci) => {
      const w = columnWidths[ci] ?? MIN_COL_WIDTH;
      return `<td contenteditable="true" style="min-width:${w}px;max-width:${w}px;">${esc(val)}</td>`;
    }).join("");
    return `<tr${cls}>${cells}</tr>`;
  }).join("");
  const tableClass = hasLeft ? "pasted-table label-left" : "pasted-table";
  const table = `<table class="${tableClass}">${colgroup}${thead}<tbody>${bodyRows}</tbody></table>`;
  return [`<div class="pasted-table-wrap" id="${uid}" contenteditable="false" data-label-pos="${labelPos}" data-labels-on="${labelsOn}" data-wrap="on">`, `<div class="pasted-table-scroll">${table}</div>`, `</div>`].join("");
}

// ── Toolbar (created per-wrap) ──

function createToolbar(wrap, editorEl) {
  const bar = document.createElement("div");
  bar.className = "pt-toolbar";
  bar.setAttribute("contenteditable", "false");
  Object.assign(bar.style, {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "4px",
    padding: "4px",
    background: "#f5f5f5",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "12px",
    color: "#333",
    userSelect: "none",
    width: "fit-content",
    boxSizing: "border-box",
    position: "absolute",
    top: "0",
    left: "0",
    transform: "translateX(-100%)",
    zIndex: "50",
    marginRight: "6px"
  });

  // Checkbox: Wrap text
  const wrapLabel = document.createElement("label");
  Object.assign(wrapLabel.style, {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "2px 4px",
    whiteSpace: "nowrap"
  });
  const wrapChk = document.createElement("input");
  wrapChk.type = "checkbox";
  wrapChk.className = "pt-wrap-check";
  wrapChk.checked = wrap.getAttribute("data-wrap") !== "off";
  const wrapSpan = document.createElement("span");
  wrapSpan.textContent = "Wrap";
  wrapSpan.style.fontSize = "12px";
  wrapLabel.appendChild(wrapChk);
  wrapLabel.appendChild(wrapSpan);
  wrapChk.addEventListener("change", () => {
    wrap.setAttribute("data-wrap", wrapChk.checked ? "on" : "off");
    const tbl = tblOf(wrap);
    if (tbl) {
      tbl.querySelectorAll("colgroup col").forEach((col, idx) => {
        const width = parseInt(col.style.width, 10) || MIN_COL_WIDTH;
        recalcCol(tbl, idx, width);
      });
    }
    editorEl.dispatchEvent(new Event("input", {
      bubbles: true
    }));
  });

  // Add column
  const addBtn = document.createElement("button");
  addBtn.className = "pt-add-col-btn";
  addBtn.innerHTML = `<svg width="11" height="11" viewBox="0 0 14 14" fill="none" style="margin-right:3px;flex-shrink:0"><line x1="7" y1="2" x2="7" y2="12" stroke="#fff" stroke-width="2" stroke-linecap="round"/><line x1="2" y1="7" x2="12" y2="7" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>Col`;
  addBtn.title = "Add column";
  Object.assign(addBtn.style, {
    height: "24px",
    borderRadius: "12px",
    border: "none",
    background: "#4caf50",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px 0 5px",
    color: "#fff",
    fontSize: "11px",
    fontWeight: "600",
    boxShadow: "0 1px 3px rgba(0,0,0,.2)",
    whiteSpace: "nowrap"
  });
  addBtn.addEventListener("click", () => {
    const tbl = tblOf(wrap);
    if (!tbl) return;
    addColumnToTable(tbl);
    injectDeleteButtons(tbl);
    editorEl.dispatchEvent(new Event("input", {
      bubbles: true
    }));
  });

  // Add row
  const addRowBtn = document.createElement("button");
  addRowBtn.className = "pt-add-row-btn";
  addRowBtn.innerHTML = `<svg width="11" height="11" viewBox="0 0 14 14" fill="none" style="margin-right:3px;flex-shrink:0"><line x1="7" y1="2" x2="7" y2="12" stroke="#fff" stroke-width="2" stroke-linecap="round"/><line x1="2" y1="7" x2="12" y2="7" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>Row`;
  addRowBtn.title = "Add row";
  Object.assign(addRowBtn.style, {
    height: "24px",
    borderRadius: "12px",
    border: "none",
    background: "#1976d2",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px 0 5px",
    color: "#fff",
    fontSize: "11px",
    fontWeight: "600",
    boxShadow: "0 1px 3px rgba(0,0,0,.2)",
    whiteSpace: "nowrap"
  });
  addRowBtn.addEventListener("click", () => {
    const tbl = tblOf(wrap);
    if (!tbl) return;
    addRowToTable(tbl);
    injectDeleteButtons(tbl);
    editorEl.dispatchEvent(new Event("input", {
      bubbles: true
    }));
  });

  // Delete table
  const delBtn = document.createElement("button");
  delBtn.className = "pt-delete-btn";
  delBtn.innerHTML = `🗑`;
  delBtn.title = "Delete table";
  Object.assign(delBtn.style, {
    height: "24px",
    borderRadius: "12px",
    border: "1px solid #d32f2f",
    background: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    fontSize: "12px",
    lineHeight: "1",
    boxShadow: "0 1px 3px rgba(0,0,0,.15)",
    color: "#d32f2f",
    whiteSpace: "nowrap"
  });
  delBtn.addEventListener("click", () => {
    if (!confirm("Delete this table?")) return;
    wrap.remove();
    editorEl.dispatchEvent(new Event("input", {
      bubbles: true
    }));
  });
  bar.appendChild(wrapLabel);
  bar.appendChild(addBtn);
  bar.appendChild(addRowBtn);
  bar.appendChild(delBtn);
  wrap.insertBefore(bar, wrap.firstChild);
  return bar;
}

// ── Drag state ──

let _drag = null;

// ── Reorder overlay state ──
let _reorderOverlay = null;
let _hideReorderTimer = null;
let _reorderTbl = null;
let _reorderCI = -1;
let _reorderEditorEl = null;

// ── Multi-cell selection state ──

let _sel = null;
let _selStyleInjected = false;

// ── Column reorder helpers ──

function isHeaderCell(el) {
  const row = el.closest("tr");
  if (!row) return false;
  return !!row.closest("thead") || row.classList.contains("default-header");
}
function getSelectedHeaderCell(editorEl) {
  if (!editorEl) return null;
  return editorEl.querySelector('[data-cell-selected="true"]');
}
function isCellColorPopupOpen() {
  return !!_cellColorPopup && _cellColorPopup.style.display === "grid";
}
function hideReorderOverlay(force = false) {
  // Keep the overlay visible while a header cell is click-selected so the
  // user can reach the color picker button without re-hovering the cell.
  if (!force && getSelectedHeaderCell(_reorderEditorEl)) return;
  // Also keep visible while the color popup is open so the user can move
  // the mouse from the swatch button down into the popup without losing it.
  if (!force && isCellColorPopupOpen()) return;
  if (_reorderOverlay) _reorderOverlay.style.display = "none";
  hideCellColorPopup();
  _reorderTbl = null;
  _reorderCI = -1;
}

// ── Header-cell color picker (palette popup attached to overlay) ──

let _cellColorPopup = null;
const PALETTE_HUES = ["rose", "red", "orange", "yellow", "green", "blue", "purple"];
function buildCellPalette() {
  // Three rows: light, primary, dark. One neutral column appended per row.
  const lights = PALETTE_HUES.map(h => ({
    bg: lightPalette[h],
    fg: "#000000"
  }));
  lights.push({
    bg: "#ffffff",
    fg: "#000000"
  });
  const primaries = PALETTE_HUES.map(h => {
    const bg = colors[h].primary;
    return {
      bg,
      fg: getContrastForBackground(bg) ?? "#000000"
    };
  });
  primaries.push({
    bg: "#9e9e9e",
    fg: "#ffffff"
  });
  const darks = PALETTE_HUES.map(h => ({
    bg: darkPalette[h],
    fg: "#ffffff"
  }));
  darks.push({
    bg: "#000000",
    fg: "#ffffff"
  });
  return [lights, primaries, darks];
}
function hideCellColorPopup() {
  if (_cellColorPopup) _cellColorPopup.style.display = "none";
}
function applyCellFill(cell, bg) {
  if (bg === null) {
    cell.style.removeProperty("background-color");
    cell.style.removeProperty("color");
    if (!cell.getAttribute("style")) cell.removeAttribute("style");
  } else {
    cell.style.setProperty("background-color", bg);
    const fg = getContrastForBackground(bg);
    if (fg) cell.style.setProperty("color", fg);
  }
}
function getActiveHeaderCell() {
  // Prefer the click-selected cell; fall back to the cell currently under the
  // hover-driven reorder overlay.
  const sel = getSelectedHeaderCell(_reorderEditorEl);
  if (sel) return sel;
  if (_reorderTbl && _reorderCI >= 0) {
    const headerRow = _reorderTbl.querySelector("tr");
    return headerRow?.children[_reorderCI] || null;
  }
  return null;
}
function ensureCellColorPopup() {
  if (_cellColorPopup) return _cellColorPopup;
  const el = document.createElement("div");
  el.className = "pt-cell-color-popup";
  Object.assign(el.style, {
    position: "fixed",
    display: "none",
    zIndex: "310",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "6px",
    boxShadow: "0 2px 8px rgba(0,0,0,.18)"
  });
  // Invisible bridge above the popup so hovering across the small visual
  // gap between the swatch button and the popup keeps the cursor "inside"
  // the popup. Without this, mouseleave of the overlay fires while the
  // mouse is still in transit and the popup is dismissed before the user
  // can interact with it.
  const bridge = document.createElement("div");
  Object.assign(bridge.style, {
    position: "absolute",
    left: "-12px",
    right: "-12px",
    top: "-12px",
    height: "12px",
    background: "transparent"
  });
  el.appendChild(bridge);
  // While the cursor is over the popup (or its bridge), cancel any pending
  // overlay-hide timer so the parent reorder bar stays anchored.
  el.addEventListener("mouseenter", () => {
    if (_hideReorderTimer !== null) {
      clearTimeout(_hideReorderTimer);
      _hideReorderTimer = null;
    }
  });
  // Build grid
  const palette = buildCellPalette();
  const cols = palette[0].length;
  Object.assign(el.style, {
    display: "none",
    gridTemplateColumns: `repeat(${cols}, 20px)`,
    gap: "4px"
  });
  palette.forEach(row => {
    row.forEach(s => {
      const sw = document.createElement("button");
      sw.type = "button";
      sw.title = s.bg;
      Object.assign(sw.style, {
        width: "20px",
        height: "20px",
        borderRadius: "4px",
        border: s.bg.toLowerCase() === "#ffffff" ? "1px solid #ccc" : "1px solid rgba(0,0,0,.15)",
        background: s.bg,
        cursor: "pointer",
        padding: "0"
      });
      sw.addEventListener("mousedown", e => e.preventDefault());
      sw.addEventListener("click", () => {
        const cell = getActiveHeaderCell();
        if (!cell) return;
        applyCellFill(cell, s.bg);
        _reorderEditorEl?.dispatchEvent(new Event("input", {
          bubbles: true
        }));
        hideCellColorPopup();
      });
      el.appendChild(sw);
    });
  });
  // Trailing "clear" row
  const clear = document.createElement("button");
  clear.type = "button";
  clear.textContent = "Clear fill";
  Object.assign(clear.style, {
    gridColumn: `1 / span ${cols}`,
    marginTop: "4px",
    padding: "4px 8px",
    fontSize: "12px",
    background: "#f5f5f5",
    border: "1px solid #ddd",
    borderRadius: "4px",
    cursor: "pointer"
  });
  clear.addEventListener("mousedown", e => e.preventDefault());
  clear.addEventListener("click", () => {
    const cell = getActiveHeaderCell();
    if (!cell) return;
    applyCellFill(cell, null);
    _reorderEditorEl?.dispatchEvent(new Event("input", {
      bubbles: true
    }));
    hideCellColorPopup();
  });
  el.appendChild(clear);
  document.body.appendChild(el);
  _cellColorPopup = el;
  return el;
}
function toggleCellColorPopup(anchor) {
  const el = ensureCellColorPopup();
  if (el.style.display === "grid") {
    hideCellColorPopup();
    return;
  }
  el.style.display = "grid";
  // Position below the anchor button, centered.
  const r = anchor.getBoundingClientRect();
  const w = el.offsetWidth || 180;
  el.style.left = `${Math.round(r.left + r.width / 2 - w / 2)}px`;
  el.style.top = `${Math.round(r.bottom + 6)}px`;
}
function ensureReorderOverlay() {
  if (_reorderOverlay) return _reorderOverlay;
  const el = document.createElement("div");
  el.className = "pt-reorder-overlay";
  Object.assign(el.style, {
    position: "fixed",
    display: "none",
    zIndex: "300",
    flexDirection: "row",
    gap: "4px",
    pointerEvents: "auto"
  });
  const mkBtn = (text, title, cls) => {
    const btn = document.createElement("button");
    btn.className = cls;
    btn.textContent = text;
    btn.title = title;
    btn.type = "button";
    Object.assign(btn.style, {
      width: "22px",
      height: "22px",
      borderRadius: "50%",
      border: "1px solid #1976d2",
      background: "#fff",
      cursor: "pointer",
      fontSize: "13px",
      lineHeight: "1",
      textAlign: "center",
      padding: "0",
      boxShadow: "0 1px 3px rgba(0,0,0,.2)",
      color: "#1976d2"
    });
    return btn;
  };
  const leftBtn = mkBtn("←", "Move column left", "pt-col-left");
  const rightBtn = mkBtn("→", "Move column right", "pt-col-right");
  const deleteBtn = mkBtn("×", "Delete column", "pt-col-delete");
  // Color picker swatch button — sits between the arrows so the user can
  // open the cell-fill palette directly from the column header.
  const colorBtn = document.createElement("button");
  colorBtn.type = "button";
  colorBtn.className = "pt-col-color";
  colorBtn.title = "Cell fill color";
  Object.assign(colorBtn.style, {
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    border: "1px solid #1976d2",
    background: "linear-gradient(135deg, #f44336 0%, #ffcc00 33%, #4caf50 66%, #2196f3 100%)",
    cursor: "pointer",
    padding: "0",
    boxShadow: "0 1px 3px rgba(0,0,0,.2)"
  });
  colorBtn.addEventListener("mousedown", e => e.preventDefault());
  colorBtn.addEventListener("click", () => {
    toggleCellColorPopup(colorBtn);
  });
  // Override delete button color scheme to indicate destructive action.
  Object.assign(deleteBtn.style, {
    border: "1px solid #d32f2f",
    background: "#d32f2f",
    color: "#fff",
    fontSize: "16px",
    fontWeight: "700",
    lineHeight: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  });
  // Prevent mousedown on overlay buttons from blurring editor
  leftBtn.addEventListener("mousedown", e => e.preventDefault());
  rightBtn.addEventListener("mousedown", e => e.preventDefault());
  deleteBtn.addEventListener("mousedown", e => e.preventDefault());
  deleteBtn.addEventListener("click", () => {
    if (!_reorderTbl || _reorderCI < 0) return;
    const cols = _reorderTbl.querySelectorAll("colgroup col").length;
    if (cols <= 1) return;
    if (!window.confirm("Delete this column? This cannot be undone.")) return;
    const removedCi = _reorderCI;
    removeColumnFromTable(_reorderTbl, removedCi);
    // Update or clear selection if it referenced this table.
    if (_sel && _sel.tbl === _reorderTbl) {
      const adjust = c => c === removedCi ? -1 : c > removedCi ? c - 1 : c;
      const a = adjust(_sel.anchorCol);
      const b = adjust(_sel.curCol);
      if (a < 0 || b < 0) {
        _sel = null;
      } else {
        _sel.anchorCol = a;
        _sel.curCol = b;
        applySelectionHighlight(_sel.tbl, _sel.anchorRow, _sel.anchorCol, _sel.curRow, _sel.curCol);
      }
    }
    _reorderEditorEl?.dispatchEvent(new Event("input", {
      bubbles: true
    }));
    hideReorderOverlay();
  });
  leftBtn.addEventListener("click", () => {
    if (_reorderTbl && _reorderCI > 0) {
      const lo = _reorderCI - 1;
      const hi = _reorderCI;
      swapColumns(_reorderTbl, lo, hi);
      _reorderCI--;
      // Update selection indices if this table's columns were involved
      if (_sel && _sel.tbl === _reorderTbl) {
        const swapCol = c => c === lo ? hi : c === hi ? lo : c;
        _sel.anchorCol = swapCol(_sel.anchorCol);
        _sel.curCol = swapCol(_sel.curCol);
        applySelectionHighlight(_sel.tbl, _sel.anchorRow, _sel.anchorCol, _sel.curRow, _sel.curCol);
      }
      _reorderEditorEl?.dispatchEvent(new Event("input", {
        bubbles: true
      }));
      const tblRows = _reorderTbl.querySelectorAll("tr");
      const headerRow = tblRows[0];
      if (headerRow) {
        const cell = headerRow.children[_reorderCI];
        if (cell) positionReorderOverlay(cell, _reorderTbl);
      }
    }
  });
  rightBtn.addEventListener("click", () => {
    if (_reorderTbl) {
      const cols = _reorderTbl.querySelectorAll("colgroup col").length;
      if (_reorderCI < cols - 1) {
        const lo = _reorderCI;
        const hi = _reorderCI + 1;
        swapColumns(_reorderTbl, lo, hi);
        _reorderCI++;
        // Update selection indices if this table's columns were involved
        if (_sel && _sel.tbl === _reorderTbl) {
          const swapCol = c => c === lo ? hi : c === hi ? lo : c;
          _sel.anchorCol = swapCol(_sel.anchorCol);
          _sel.curCol = swapCol(_sel.curCol);
          applySelectionHighlight(_sel.tbl, _sel.anchorRow, _sel.anchorCol, _sel.curRow, _sel.curCol);
        }
        _reorderEditorEl?.dispatchEvent(new Event("input", {
          bubbles: true
        }));
        const tblRows = _reorderTbl.querySelectorAll("tr");
        const headerRow = tblRows[0];
        if (headerRow) {
          const cell = headerRow.children[_reorderCI];
          if (cell) positionReorderOverlay(cell, _reorderTbl);
        }
      }
    }
  });
  el.addEventListener("mouseenter", () => {
    if (_hideReorderTimer !== null) {
      clearTimeout(_hideReorderTimer);
      _hideReorderTimer = null;
    }
  });
  el.addEventListener("mouseleave", () => {
    _hideReorderTimer = setTimeout(() => {
      hideReorderOverlay();
      _hideReorderTimer = null;
    }, 150);
  });
  el.appendChild(leftBtn);
  el.appendChild(colorBtn);
  el.appendChild(deleteBtn);
  el.appendChild(rightBtn);
  document.body.appendChild(el);
  _reorderOverlay = el;
  return el;
}
function positionReorderOverlay(cell, tbl) {
  const ov = _reorderOverlay;
  if (!ov) return;
  const r = cell.getBoundingClientRect();
  const ci = cellColIdx(cell);
  const cols = tbl.querySelectorAll("colgroup col").length;
  ov.style.display = "flex";
  const leftBtn = ov.querySelector(".pt-col-left");
  const rightBtn = ov.querySelector(".pt-col-right");
  const deleteBtn = ov.querySelector(".pt-col-delete");
  // Always show delete column in the overlay as long as there is more than one column.
  const canDelete = cols > 1;
  deleteBtn.style.display = canDelete ? "inline-flex" : "none";
  // Layout: ← + color + × + → = 4 buttons × 22px + 3 gaps × 4px = 100px.
  // Without delete: 3 × 22 + 2 × 4 = 74px.
  const ovWidth = canDelete ? 100 : 74;
  ov.style.left = `${Math.round(r.left + r.width / 2 - ovWidth / 2)}px`;
  ov.style.top = `${Math.round(r.top - 28)}px`;
  leftBtn.style.opacity = ci === 0 ? "0.35" : "1";
  leftBtn.style.pointerEvents = ci === 0 ? "none" : "auto";
  rightBtn.style.opacity = ci >= cols - 1 ? "0.35" : "1";
  rightBtn.style.pointerEvents = ci >= cols - 1 ? "none" : "auto";
}

/** Swap two adjacent (or non-adjacent) columns in a table (colgroup + all rows). */
function swapColumns(tbl, lo, hi) {
  if (lo === hi) return;
  const cg = tbl.querySelector("colgroup");
  if (cg) {
    const cols = Array.from(cg.children);
    if (lo < cols.length && hi < cols.length) {
      const loEl = cols[lo];
      const hiEl = cols[hi];
      const hiNext = hiEl.nextSibling;
      cg.insertBefore(hiEl, loEl);
      if (hiNext) cg.insertBefore(loEl, hiNext);else cg.appendChild(loEl);
    }
  }
  tbl.querySelectorAll("tr").forEach(tr => {
    const cells = Array.from(tr.children);
    if (lo < cells.length && hi < cells.length) {
      const loEl = cells[lo];
      const hiEl = cells[hi];
      const hiNext = hiEl.nextSibling;
      tr.insertBefore(hiEl, loEl);
      if (hiNext) tr.insertBefore(loEl, hiNext);else tr.appendChild(loEl);
    }
  });
}

/** Remove a column (colgroup col + matching cell on every row) at index ci. */
function removeColumnFromTable(tbl, ci) {
  const cg = tbl.querySelector("colgroup");
  if (cg && cg.children[ci]) cg.children[ci].remove();
  tbl.querySelectorAll("tr").forEach(tr => {
    const cell = tr.children[ci];
    if (cell) cell.remove();
  });
}

// ── Multi-cell selection helpers ──

function cellRowIdx(cell) {
  const row = cell.closest("tr");
  if (!row) return -1;
  const tbl = cell.closest("table");
  if (!tbl) return -1;
  return Array.from(tbl.querySelectorAll("tr")).indexOf(row);
}
function injectSelectionStyles() {
  if (_selStyleInjected) return;
  _selStyleInjected = true;
  if (document.getElementById("pt-sel-styles")) return;
  const style = document.createElement("style");
  style.id = "pt-sel-styles";
  style.textContent = `[data-cell-selected="true"] { outline: 2px solid #1976d2 !important; outline-offset: -1px; box-shadow: inset 0 0 0 1px #1976d2; }` +
  // Hover-triggered row delete affordance (top-left of first body cell)
  `.pasted-table-wrap td, .pasted-table-wrap th { position: relative; }` + `.pt-del-row { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #d32f2f; color: #fff; font-size: 11px; line-height: 16px; text-align: center; font-weight: 700; cursor: pointer; opacity: 0; transition: opacity .12s; user-select: none; box-shadow: 0 1px 3px rgba(0,0,0,0.3); z-index: 4; pointer-events: none; }` + `.pt-del-row:hover { background: #b71c1c; transform: scale(1.1); }`;
  document.head.appendChild(style);
}

/**
 * Inject hover-triggered "delete row" X buttons into the first body cell of
 * every data row.  Re-callable safely — duplicates are skipped.
 * Delete column is handled via the reorder overlay (see ensureReorderOverlay).
 */
function injectDeleteButtons(tbl) {
  // Row delete: first cell of every body row (excluding the header row when no thead).
  const bodyRows = [];
  const tbody = tbl.querySelector("tbody");
  if (tbody) {
    bodyRows.push(...Array.from(tbody.querySelectorAll("tr")));
  } else {
    const all = Array.from(tbl.querySelectorAll("tr"));
    // Skip the first row (treated as header).
    bodyRows.push(...all.slice(1));
  }
  bodyRows.forEach(tr => {
    const firstCell = tr.children[0];
    if (!firstCell) return;
    if (firstCell.querySelector(":scope > .pt-del-row")) return;
    const btn = document.createElement("span");
    btn.className = "pt-del-row";
    btn.setAttribute("data-pt-del", "row");
    btn.setAttribute("contenteditable", "false");
    btn.setAttribute("title", "Delete row");
    btn.textContent = "\u00d7";
    firstCell.appendChild(btn);
  });
}

/** Remove a row from the table at index ri (counting body rows only). */
function removeRowFromTable(tbl, tr) {
  tr.remove();
}
function clearAllSelections(editorEl) {
  editorEl.querySelectorAll(".pt-selected").forEach(el => el.classList.remove("pt-selected"));
  _sel = null;
}
function applySelectionHighlight(tbl, r1, c1, r2, c2) {
  const rows = Array.from(tbl.querySelectorAll("tr"));
  const minR = Math.min(r1, r2),
    maxR = Math.max(r1, r2);
  const minC = Math.min(c1, c2),
    maxC = Math.max(c1, c2);
  rows.forEach((row, ri) => {
    Array.from(row.children).forEach((cell, ci) => {
      if (ri >= minR && ri <= maxR && ci >= minC && ci <= maxC) {
        cell.classList.add("pt-selected");
      } else {
        cell.classList.remove("pt-selected");
      }
    });
  });
}
function serializeSelection(tbl, r1, c1, r2, c2) {
  const rows = Array.from(tbl.querySelectorAll("tr"));
  const minR = Math.min(r1, r2),
    maxR = Math.max(r1, r2);
  const minC = Math.min(c1, c2),
    maxC = Math.max(c1, c2);
  const matrix = [];
  // Parallel structured cells with style + innerHTML for HTML clipboard payload
  const richMatrix = [];
  for (let ri = minR; ri <= maxR; ri++) {
    const row = rows[ri];
    if (!row) continue;
    const rowData = [];
    const richRow = [];
    for (let ci = minC; ci <= maxC; ci++) {
      const cell = row.children[ci];
      rowData.push(cell ? cell.textContent || "" : "");
      if (cell) {
        // Carry over color + background-color from inline style for Docs roundtrip
        const color = cell.style.color;
        const bg = cell.style.backgroundColor;
        const styleParts = [];
        if (color) styleParts.push(`color: ${color}`);
        if (bg) styleParts.push(`background-color: ${bg}`);
        richRow.push({
          style: styleParts.join("; "),
          html: cell.innerHTML || ""
        });
      } else {
        richRow.push({
          style: "",
          html: ""
        });
      }
    }
    matrix.push(rowData);
    richMatrix.push(richRow);
  }
  const tsv = matrix.map(r => r.join("\t")).join("\n");
  const htmlRows = richMatrix.map(r => `<tr>${r.map(c => `<td${c.style ? ` style="${c.style}"` : ""}>${c.html}</td>`).join("")}</tr>`).join("");
  return {
    tsv,
    html: `<table><tbody>${htmlRows}</tbody></table>`
  };
}
function onDocMove(e) {
  if (!_drag) return;
  ensureGhostLine().style.left = `${e.clientX}px`;
}
function onDocUp(e) {
  if (!_drag) return;
  const {
    ci,
    x0,
    w0,
    tbl,
    ed
  } = _drag;
  recalcCol(tbl, ci, Math.max(MIN_COL_WIDTH, w0 + (e.clientX - x0)));
  ed.dispatchEvent(new Event("input", {
    bubbles: true
  }));
  _drag = null;
  ensureGhostLine().style.display = "none";
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
}

// ── Main attachment ──

export function attachTableInteractions(editorEl) {
  // Track this editor for reorder overlay dispatch
  _reorderEditorEl = editorEl;
  const initWrap = wrap => {
    const old = wrap.querySelector(".pasted-table-label-prompt");
    if (old) old.remove();
    if (!wrap.hasAttribute("data-label-pos")) wrap.setAttribute("data-label-pos", "top");
    if (!wrap.hasAttribute("data-labels-on")) wrap.setAttribute("data-labels-on", "false");
    if (!wrap.hasAttribute("data-wrap")) wrap.setAttribute("data-wrap", "on");
    if (!wrap.querySelector(".pt-toolbar")) createToolbar(wrap, editorEl);
    wrap.style.position = "relative";
    // Inject hover-triggered row/column delete X buttons
    injectSelectionStyles();
    const tbl = tblOf(wrap);
    if (tbl) injectDeleteButtons(tbl);
  };
  const attachedWraps = new Set();
  const bindWraps = () => {
    editorEl.querySelectorAll(".pasted-table-wrap").forEach(w => {
      if (attachedWraps.has(w)) return;
      attachedWraps.add(w);
      initWrap(w);
    });
  };
  bindWraps();
  const mo = new MutationObserver(bindWraps);
  mo.observe(editorEl, {
    childList: true,
    subtree: true
  });
  const onMM = e => {
    if (_drag) return;
    const cell = e.target.closest?.("td, th");
    if (!cell || !findWrap(cell)) return;
    const r = cell.getBoundingClientRect();
    cell.style.cursor = e.clientX >= r.right - RESIZE_ZONE_PX ? "col-resize" : "";
  };
  // Helper: show/hide a row's delete button via inline style (more reliable
  // than CSS tr:hover inside contenteditable wrappers).
  let _hoveredDelRow = null;
  const showRowDelBtn = tr => {
    if (_hoveredDelRow === tr) return;
    // Hide previous row's button
    if (_hoveredDelRow) {
      const prev = _hoveredDelRow.querySelector(".pt-del-row");
      if (prev) {
        prev.style.opacity = "0";
        prev.style.pointerEvents = "none";
      }
    }
    _hoveredDelRow = tr;
    const btn = tr.querySelector(".pt-del-row");
    if (btn) {
      btn.style.opacity = "0.9";
      btn.style.pointerEvents = "auto";
    }
  };
  const hideRowDelBtn = tr => {
    if (_hoveredDelRow !== tr) return;
    _hoveredDelRow = null;
    const btn = tr.querySelector(".pt-del-row");
    if (btn) {
      btn.style.opacity = "0";
      btn.style.pointerEvents = "none";
    }
  };

  // mouseover: show reorder overlay on header cells + extend selection drag
  const onOver = e => {
    const target = e.target;
    const cell = target.closest?.("td, th");
    if (!cell) return;
    const wrap = findWrap(cell);
    if (!wrap) return;
    const tbl = tblOf(wrap);
    if (!tbl) return;
    // Reorder arrows
    if (isHeaderCell(cell)) {
      if (_hideReorderTimer !== null) {
        clearTimeout(_hideReorderTimer);
        _hideReorderTimer = null;
      }
      _reorderTbl = tbl;
      _reorderCI = cellColIdx(cell);
      ensureReorderOverlay();
      positionReorderOverlay(cell, tbl);
    } else {
      // Show the delete button on body rows
      const tr = cell.closest("tr");
      if (tr && tr.closest("tbody")) showRowDelBtn(tr);
    }
    // (multi-cell drag selection disabled — native text selection used instead)
  };
  // mouseout: schedule hide of reorder overlay when leaving header cell
  const onOut = e => {
    const cell = e.target.closest?.("td, th");
    if (!cell) return;
    const related = e.relatedTarget;
    if (isHeaderCell(cell)) {
      if (related && _reorderOverlay?.contains(related)) return;
      if (_hideReorderTimer !== null) clearTimeout(_hideReorderTimer);
      _hideReorderTimer = setTimeout(() => {
        hideReorderOverlay();
        _hideReorderTimer = null;
      }, 150);
    } else {
      // Hide the delete button when leaving a body row
      const tr = cell.closest("tr");
      if (tr) {
        // Only hide if the cursor is leaving the row entirely
        if (!related || !tr.contains(related)) hideRowDelBtn(tr);
      }
    }
  };
  const onMD = e => {
    const cell = e.target.closest?.("td, th");
    if (!cell) {
      if (_sel) clearAllSelections(editorEl);
      return;
    }
    const wrap = findWrap(cell);
    if (!wrap) {
      if (_sel) clearAllSelections(editorEl);
      return;
    }
    const tbl = tblOf(wrap);
    if (!tbl) return;
    const r = cell.getBoundingClientRect();
    if (e.clientX >= r.right - RESIZE_ZONE_PX) {
      // Resize drag
      e.preventDefault();
      const ci = cellColIdx(cell);
      const col = tbl.querySelectorAll("colgroup col")[ci];
      const w0 = col ? parseInt(col.style.width) || MIN_COL_WIDTH : MIN_COL_WIDTH;
      _drag = {
        ci,
        x0: e.clientX,
        w0,
        tbl,
        ed: editorEl
      };
      const gl = ensureGhostLine();
      const tr = tbl.getBoundingClientRect();
      gl.style.display = "block";
      gl.style.left = `${e.clientX}px`;
      gl.style.top = `${tr.top}px`;
      gl.style.height = `${tr.height}px`;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      // If header cell click → toggle data-cell-selected (single-cell pick for color tools)
      if (isHeaderCell(cell)) {
        injectSelectionStyles();
        // Always clear any multi-cell drag selection when clicking a header
        if (_sel) clearAllSelections(editorEl);
        const wasSelected = cell.getAttribute("data-cell-selected") === "true";
        // Clear all selected cells in this editor first
        editorEl.querySelectorAll('[data-cell-selected="true"]').forEach(el => el.removeAttribute("data-cell-selected"));
        if (!wasSelected) {
          cell.setAttribute("data-cell-selected", "true");
          // Pin the reorder/color overlay over this cell so the user
          // can reach the color picker without keeping the cursor
          // hovered on the header.
          if (_hideReorderTimer !== null) {
            clearTimeout(_hideReorderTimer);
            _hideReorderTimer = null;
          }
          _reorderTbl = tbl;
          _reorderCI = cellColIdx(cell);
          ensureReorderOverlay();
          positionReorderOverlay(cell, tbl);
        } else {
          // Deselected — hide picker popup and overlay.
          hideReorderOverlay(true);
        }
        // Don't clear text-range selection; allow header cell pick to coexist
        return;
      }
      // Clear any header cell pick when clicking a body cell
      editorEl.querySelectorAll('[data-cell-selected="true"]').forEach(el => el.removeAttribute("data-cell-selected"));
      hideReorderOverlay(true);
      // Let native browser text selection handle the rest
    }
  };
  // Copy: serialize selected cells to TSV + HTML
  const onCopy = e => {
    if (!_sel) return;
    const {
      tsv,
      html
    } = serializeSelection(_sel.tbl, _sel.anchorRow, _sel.anchorCol, _sel.curRow, _sel.curCol);
    e.preventDefault();
    e.clipboardData?.setData("text/plain", tsv);
    e.clipboardData?.setData("text/html", html);
  };
  // Cut: same as copy + clear cells
  const onCut = e => {
    if (!_sel) return;
    const {
      tsv,
      html
    } = serializeSelection(_sel.tbl, _sel.anchorRow, _sel.anchorCol, _sel.curRow, _sel.curCol);
    e.preventDefault();
    e.clipboardData?.setData("text/plain", tsv);
    e.clipboardData?.setData("text/html", html);
    const rows = Array.from(_sel.tbl.querySelectorAll("tr"));
    const minR = Math.min(_sel.anchorRow, _sel.curRow);
    const maxR = Math.max(_sel.anchorRow, _sel.curRow);
    const minC = Math.min(_sel.anchorCol, _sel.curCol);
    const maxC = Math.max(_sel.anchorCol, _sel.curCol);
    for (let ri = minR; ri <= maxR; ri++) {
      const row = rows[ri];
      if (!row) continue;
      for (let ci = minC; ci <= maxC; ci++) {
        const cell = row.children[ci];
        if (cell) cell.textContent = "";
      }
    }
    editorEl.dispatchEvent(new Event("input", {
      bubbles: true
    }));
  };
  // Paste TSV into existing table when focus is inside a table cell
  const onPaste = e => {
    const active = document.activeElement;
    if (!active?.closest?.(".pasted-table-wrap")) return;
    const text = e.clipboardData?.getData("text/plain") ?? "";
    if (!text.includes("\t")) return;
    // Stop outer new-table creation handler
    e.stopPropagation();
    e.preventDefault();
    // Anchor: use selection top-left or focused cell
    const anchorCell = _sel ? (() => {
      const tblRows = Array.from(_sel.tbl.querySelectorAll("tr"));
      const r = Math.min(_sel.anchorRow, _sel.curRow);
      const c = Math.min(_sel.anchorCol, _sel.curCol);
      return tblRows[r]?.children[c];
    })() : active?.closest("td, th");
    if (!anchorCell) return;
    const anchorTbl = anchorCell.closest("table");
    if (!anchorTbl) return;
    const startRow = cellRowIdx(anchorCell);
    const startCol = cellColIdx(anchorCell);
    const {
      rows: matrix
    } = parseTsv(text);
    // Append rows if paste extends past table bounds
    const neededRows = startRow + matrix.length;
    const existingRows = anchorTbl.querySelectorAll("tr").length;
    for (let i = existingRows; i < neededRows; i++) {
      addRowToTable(anchorTbl);
    }
    const tblRows = Array.from(anchorTbl.querySelectorAll("tr"));
    matrix.forEach((row, ri) => {
      const tblRow = tblRows[startRow + ri];
      if (!tblRow) return;
      row.forEach((val, ci) => {
        const cell = tblRow.children[startCol + ci];
        if (cell && cell.getAttribute("contenteditable") === "true") {
          cell.textContent = val;
        }
      });
    });
    editorEl.dispatchEvent(new Event("input", {
      bubbles: true
    }));
  };
  // Escape: clear header cell selection
  const onKeyDown = e => {
    if (e.key === "Escape") {
      editorEl.querySelectorAll('[data-cell-selected="true"]').forEach(el => el.removeAttribute("data-cell-selected"));
      hideReorderOverlay(true);
    }
  };
  // Clear selection when clicking outside the editor entirely
  const onDocMD = e => {
    const target = e.target;
    const insideEditor = editorEl.contains(target);
    const insideOverlay = !!_reorderOverlay?.contains(target);
    const insidePopup = !!_cellColorPopup?.contains(target);
    // If click is outside editor, overlay and popup, clear cell pick + multi-sel
    if (!insideEditor && !insideOverlay && !insidePopup) {
      editorEl.querySelectorAll('[data-cell-selected="true"]').forEach(el => el.removeAttribute("data-cell-selected"));
      hideReorderOverlay(true);
      // Don't clear if clicking the toolbar (which is part of the editor host but
      // sometimes outside editorEl). The cell-pick should survive toolbar clicks.
      if (_sel) clearAllSelections(editorEl);
      return;
    }
    // Click inside editor but not on the picker popup → hide the popup.
    if (!insidePopup && !insideOverlay) {
      hideCellColorPopup();
    }
  };

  // Forward input events from nested contenteditable cells inside
  // contenteditable="false" table wraps so the editor's onInput fires.
  const onCellInput = e => {
    const target = e.target;
    if (target.closest?.(".pasted-table-wrap") && target !== editorEl) {
      editorEl.dispatchEvent(new Event("input", {
        bubbles: true
      }));
    }
  };
  editorEl.addEventListener("input", onCellInput, true); // capture phase

  // ── Custom 500ms tooltip for truncated cells ────────────────────────
  // When wrap is off, recalcCol writes the cell's full text to
  // `data-full-text`. We render a custom tooltip after a fixed 500ms
  // hover delay (the native `title` attribute uses a much longer,
  // platform-dependent delay that felt sluggish to users).
  let tooltipEl = null;
  let tooltipShowTimer = null;
  let tooltipCurrentCell = null;
  const ensureTooltipEl = () => {
    if (tooltipEl) return tooltipEl;
    const el = document.createElement("div");
    el.className = "pt-cell-tooltip";
    Object.assign(el.style, {
      position: "fixed",
      display: "none",
      zIndex: "320",
      background: "rgba(33, 33, 33, 0.95)",
      color: "#fff",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      lineHeight: "1.4",
      maxWidth: "320px",
      wordBreak: "break-word",
      pointerEvents: "none",
      boxShadow: "0 2px 8px rgba(0,0,0,0.18)"
    });
    document.body.appendChild(el);
    tooltipEl = el;
    return el;
  };
  const hideCellTooltip = () => {
    if (tooltipShowTimer !== null) {
      clearTimeout(tooltipShowTimer);
      tooltipShowTimer = null;
    }
    tooltipCurrentCell = null;
    if (tooltipEl) tooltipEl.style.display = "none";
  };
  const showCellTooltip = (cell, x, y) => {
    const text = cell.getAttribute("data-full-text");
    if (!text) return;
    const el = ensureTooltipEl();
    el.textContent = text;
    el.style.display = "block";
    // Position just below the cursor; clamp to viewport.
    const margin = 12;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const maxLeft = window.innerWidth - w - 4;
    const maxTop = window.innerHeight - h - 4;
    el.style.left = `${Math.max(4, Math.min(maxLeft, x + margin))}px`;
    el.style.top = `${Math.max(4, Math.min(maxTop, y + margin))}px`;
  };
  const onTooltipMove = e => {
    const cell = e.target.closest?.("td, th");
    if (!cell || !findWrap(cell)) {
      hideCellTooltip();
      return;
    }
    if (!cell.hasAttribute("data-full-text")) {
      hideCellTooltip();
      return;
    }
    // Same cell: keep tooltip anchored to current cursor; don't reset timer.
    if (cell === tooltipCurrentCell) {
      if (tooltipEl && tooltipEl.style.display === "block") {
        showCellTooltip(cell, e.clientX, e.clientY);
      }
      return;
    }
    // Hover entered a new truncated cell: schedule the 500ms reveal.
    hideCellTooltip();
    tooltipCurrentCell = cell;
    const cx = e.clientX;
    const cy = e.clientY;
    tooltipShowTimer = setTimeout(() => {
      tooltipShowTimer = null;
      if (tooltipCurrentCell === cell) {
        showCellTooltip(cell, cx, cy);
      }
    }, 500);
  };
  const onTooltipLeave = () => hideCellTooltip();
  editorEl.addEventListener("mousemove", onTooltipMove);
  editorEl.addEventListener("mouseleave", onTooltipLeave);

  // Click delegation for hover-triggered row delete X buttons.
  const onDelClick = e => {
    const target = e.target;
    const btn = target.closest?.(".pt-del-row");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const cell = btn.closest("td, th");
    const tbl = cell?.closest("table");
    if (!cell || !tbl) return;
    const tr = cell.closest("tr");
    if (!tr) return;
    // Never allow deleting a header row
    if (tr.closest("thead")) {
      return;
    }
    const tbody = tbl.querySelector("tbody") || tbl;
    const bodyRows = Array.from(tbody.querySelectorAll("tr"));
    // Don't allow deleting the last remaining body row.
    if (bodyRows.length <= 1) {
      window.alert("Cannot delete the last row.");
      return;
    }
    if (!window.confirm("Delete this row? This cannot be undone.")) return;
    removeRowFromTable(tbl, tr);
    injectDeleteButtons(tbl);
    editorEl.dispatchEvent(new InputEvent("input", {
      bubbles: true
    }));
  };
  editorEl.addEventListener("click", onDelClick, true);
  editorEl.addEventListener("mousemove", onMM);
  editorEl.addEventListener("mouseover", onOver);
  editorEl.addEventListener("mouseout", onOut);
  editorEl.addEventListener("mousedown", onMD);
  editorEl.addEventListener("copy", onCopy);
  editorEl.addEventListener("cut", onCut);
  editorEl.addEventListener("paste", onPaste);
  editorEl.addEventListener("keydown", onKeyDown);
  document.addEventListener("mousemove", onDocMove);
  document.addEventListener("mouseup", onDocUp);
  document.addEventListener("mousedown", onDocMD);

  // Hide the floating header overlay (reorder arrows + color picker) on
  // any scroll. The overlay is positioned in fixed/viewport coordinates
  // anchored to the header cell's bounding rect; once the page scrolls
  // it would otherwise stay pinned to its old viewport position while
  // the table moves underneath it.
  const onScrollHide = () => {
    if (_hideReorderTimer !== null) {
      clearTimeout(_hideReorderTimer);
      _hideReorderTimer = null;
    }
    hideReorderOverlay(true);
  };
  // Capture-phase listener catches scrolls on any ancestor scroll
  // container (e.g. the editor's own scroll area), not just the window.
  window.addEventListener("scroll", onScrollHide, true);
  return () => {
    mo.disconnect();
    attachedWraps.clear();
    editorEl.removeEventListener("input", onCellInput, true);
    editorEl.removeEventListener("mousemove", onTooltipMove);
    editorEl.removeEventListener("mouseleave", onTooltipLeave);
    hideCellTooltip();
    if (tooltipEl) {
      tooltipEl.remove();
      tooltipEl = null;
    }
    editorEl.removeEventListener("mousemove", onMM);
    editorEl.removeEventListener("mouseover", onOver);
    editorEl.removeEventListener("mouseout", onOut);
    editorEl.removeEventListener("mousedown", onMD);
    editorEl.removeEventListener("copy", onCopy);
    editorEl.removeEventListener("cut", onCut);
    editorEl.removeEventListener("paste", onPaste);
    editorEl.removeEventListener("keydown", onKeyDown);
    editorEl.removeEventListener("click", onDelClick, true);
    document.removeEventListener("mousemove", onDocMove);
    document.removeEventListener("mouseup", onDocUp);
    document.removeEventListener("mousedown", onDocMD);
    window.removeEventListener("scroll", onScrollHide, true);
    clearAllSelections(editorEl);
    hideReorderOverlay();
  };
}

/**
 * Insert a `.pasted-table-wrap` HTML string into a contenteditable editor at
 * the current caret position.
 *
 * The table wrap is a block-level element (div). Inserting it with
 * Range.insertNode() would nest it inside whatever inline/block element
 * currently holds the caret (e.g. a `<p>`), producing invalid HTML.
 * Instead we walk up from the caret to the direct child of `editorEl`, insert
 * the wrap after that child, and place a fresh `<p>` after the wrap so the
 * user can keep typing below it.
 *
 * Call `setContentHTML(editorEl.innerHTML)` (or equivalent) after this
 * function to sync React state.
 */
export function insertTableHtmlAtCaret(editorEl, tableHtml) {
  const tmp = document.createElement("div");
  tmp.innerHTML = tableHtml;
  const tableWrap = tmp.firstElementChild;
  if (!tableWrap) return;
  const sel = window.getSelection();
  let insertAfter = null;
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    // Walk up to the direct child of editorEl
    let block = range.startContainer;
    while (block && block.parentNode !== editorEl) {
      block = block.parentNode;
    }
    if (block && block.parentNode === editorEl) {
      insertAfter = block;
    }
  }

  // Guard: also skip if the caret is already inside a table wrap
  if (insertAfter instanceof HTMLElement && insertAfter.classList.contains("pasted-table-wrap")) {
    insertAfter = insertAfter.nextSibling ?? null;
    if (insertAfter === null) {
      editorEl.appendChild(tableWrap);
    } else {
      editorEl.insertBefore(tableWrap, insertAfter);
    }
  } else if (insertAfter) {
    editorEl.insertBefore(tableWrap, insertAfter.nextSibling);
  } else {
    editorEl.appendChild(tableWrap);
  }

  // Insert a paragraph after the table so the user can type below it
  const after = document.createElement("p");
  after.appendChild(document.createElement("br"));
  editorEl.insertBefore(after, tableWrap.nextSibling);

  // Move caret into the paragraph
  try {
    const r = document.createRange();
    r.setStart(after, 0);
    r.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(r);
  } catch {
    // Non-fatal; caret placement is best-effort
  }
}

/**
 * Lightweight read-only tooltip for truncated table cells, intended for
 * the published regulation/article view. Mirrors the editor's custom
 * 500ms reveal but does not install any of the editor's selection,
 * resize, or reorder listeners — so it can safely run on pages that
 * render persisted table HTML via `dangerouslySetInnerHTML`.
 */
export function attachTableCellTooltip(root) {
  let tooltipEl = null;
  let tooltipShowTimer = null;
  let tooltipCurrentCell = null;
  const ensureTooltipEl = () => {
    if (tooltipEl) return tooltipEl;
    const el = document.createElement("div");
    el.className = "pt-cell-tooltip";
    Object.assign(el.style, {
      position: "fixed",
      display: "none",
      zIndex: "320",
      background: "rgba(33, 33, 33, 0.95)",
      color: "#fff",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      lineHeight: "1.4",
      maxWidth: "320px",
      wordBreak: "break-word",
      pointerEvents: "none",
      boxShadow: "0 2px 8px rgba(0,0,0,0.18)"
    });
    document.body.appendChild(el);
    tooltipEl = el;
    return el;
  };
  const hide = () => {
    if (tooltipShowTimer !== null) {
      clearTimeout(tooltipShowTimer);
      tooltipShowTimer = null;
    }
    tooltipCurrentCell = null;
    if (tooltipEl) tooltipEl.style.display = "none";
  };
  const show = (cell, x, y) => {
    const text = cell.getAttribute("data-full-text");
    if (!text) return;
    const el = ensureTooltipEl();
    el.textContent = text;
    el.style.display = "block";
    const margin = 12;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const maxLeft = window.innerWidth - w - 4;
    const maxTop = window.innerHeight - h - 4;
    el.style.left = `${Math.max(4, Math.min(maxLeft, x + margin))}px`;
    el.style.top = `${Math.max(4, Math.min(maxTop, y + margin))}px`;
  };
  const onMove = e => {
    const cell = e.target.closest?.("td, th");
    if (!cell || !cell.closest(".pasted-table-wrap")) {
      hide();
      return;
    }
    if (!cell.hasAttribute("data-full-text")) {
      hide();
      return;
    }
    if (cell === tooltipCurrentCell) {
      if (tooltipEl && tooltipEl.style.display === "block") {
        show(cell, e.clientX, e.clientY);
      }
      return;
    }
    hide();
    tooltipCurrentCell = cell;
    const cx = e.clientX;
    const cy = e.clientY;
    tooltipShowTimer = setTimeout(() => {
      tooltipShowTimer = null;
      if (tooltipCurrentCell === cell) show(cell, cx, cy);
    }, 500);
  };
  const onLeave = () => hide();
  const onScroll = () => hide();
  root.addEventListener("mousemove", onMove);
  root.addEventListener("mouseleave", onLeave);
  window.addEventListener("scroll", onScroll, true);
  return () => {
    root.removeEventListener("mousemove", onMove);
    root.removeEventListener("mouseleave", onLeave);
    window.removeEventListener("scroll", onScroll, true);
    hide();
    if (tooltipEl) {
      tooltipEl.remove();
      tooltipEl = null;
    }
  };
}